import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const LanguageContext = createContext(undefined);

const translations = {
  de: {
    // Header
    'header.login': 'Login',
    'header.start': 'Jetzt starten',

    // Cookie banner (Speicherhinweis — keine Marketing-/Analytics-Skripte im Projekt)
    'cookieBanner.ariaLabel': 'Hinweis zu Cookies und lokalem Speicher',
    'cookieBanner.lead':
      'Wir verwenden lokalen Speicher im Browser (z. B. für Anmeldung und Sprache). Es werden derzeit keine Marketing- oder Analyse-Cookies von Drittanbietern eingesetzt. Details finden Sie in der',
    'cookieBanner.privacy': 'Datenschutzerklärung',
    'cookieBanner.details': 'Details',
    'cookieBanner.acknowledge': 'Verstanden',
    'cookieBanner.settingsTitle': 'Cookies und lokaler Speicher',
    'cookieBanner.settingsIntro':
      'Nach der DSGVO zählen auch „Local Storage“ und „Session Storage“ zu den Technologien, für die Sie informiert werden. Folgende Daten werden in Ihrem Browser gespeichert:',
    'cookieBanner.storageAuthTitle': 'Anmeldung',
    'cookieBanner.storageAuthDesc':
      'Schlüssel „token“ und „user“ (localStorage) für die Sitzung nach dem Login.',
    'cookieBanner.storageLanguageTitle': 'Sprache',
    'cookieBanner.storageLanguageDesc':
      'Schlüssel „language“ (localStorage) für Ihre gewählte Oberflächensprache.',
    'cookieBanner.storageConsentTitle': 'Cookie-Hinweis',
    'cookieBanner.storageConsentDesc':
      'Schlüssel „klagma_cookie_prefs“ (localStorage) für die Kenntnisnahme dieses Hinweises.',
    'cookieBanner.storageSessionTitle': 'Sitzung (technisch)',
    'cookieBanner.storageSessionDesc':
      'Kurzlebige Einträge (sessionStorage) zur Entlastung von Abfragen während der Nutzung – keine Werbeprofile.',
    'cookieBanner.noThirdParty':
      'Derzeit werden keine Tracking- oder Marketing-Tools von Drittanbietern (z. B. Meta Pixel, Google Analytics) in diese Seite eingebunden.',
    'cookieBanner.cancel': 'Schließen',
    'cookieBanner.reopenLink': 'Cookie-Einstellungen',

    // Hero
    'hero.title': 'Vertragsanalyse von\nOnline-Verträgen',
    'hero.subtitle': 'Ein Service für professionelle Vertragsprüfung',
    'hero.cta': 'Jetzt analysieren',
    
    // Media
    'media.title': 'Bekannt aus den Medien',
    
    // Service
    'service.title': 'Geld bei Online-Verträgen verloren?',
    'service.description': 'Wir unterstützen Sie bei der Analyse Ihrer Verträge. Verluste durch ungünstige Vertragsklauseln können grundsätzlich identifiziert und vermieden werden. Mit unserer professionellen Vertragsanalyse können Sie Ihre Verträge verstehen und schützen. Wir haben bereits viele Unternehmen und Privatpersonen bei der Analyse ihrer Verträge unterstützt.',
    'service.discrete': 'Diskret',
    'service.personal': 'Persönlich',
    'service.efficient': 'Effizient',
    'service.reliable': 'Zuverlässig',
    
    // Steps
    'steps.title': 'Verträge analysieren',
    'steps.subtitle': 'Einfach, diskret und rasch anmelden – wir analysieren Ihre Verträge professionell. Mit diesen drei Schritten sichern Sie sich die Chance, Ihre Verträge zu verstehen:',
    'steps.contact.title': 'Kontaktaufnahme',
    'steps.contact.description': 'Sie nehmen über das Kontaktformular, telefonisch oder per E-Mail Kontakt mit uns auf und übermitteln uns Ihren Vertrag zur Analyse. Wir melden uns verlässlich bei Ihnen!',
    'steps.analysis.title': 'Analyse',
    'steps.analysis.description': 'Wir analysieren Ihren Vertrag, identifizieren kritische Klauseln und zeigen Ihnen potenzielle Risiken auf. Im Zuge einer kostenlosen Ersteinschätzung erfahren Sie die wichtigsten Erkenntnisse.',
    'steps.report.title': 'Detaillierter Bericht',
    'steps.report.description': 'Sie erhalten einen umfassenden Analysebericht mit allen wichtigen Erkenntnissen, Risikobewertungen und Handlungsempfehlungen. Wir unterstützen Sie bei der Interpretation der Ergebnisse.',
    
    // Why
    'why.title': 'Warum sollten Verträge analysiert werden?',
    'why.description1': 'Viele Verträge enthalten komplexe Klauseln, die für Laien schwer verständlich sind. Versteckte Kosten, ungünstige Bedingungen oder rechtliche Fallstricke können zu finanziellen Verlusten führen. Eine professionelle Vertragsanalyse hilft Ihnen, diese Risiken zu identifizieren und zu vermeiden.',
    'why.description2': 'Mit unserer KI-gestützten Analyse und juristischer Expertise setzen wir uns für Ihr Recht ein und helfen Ihnen, fundierte Entscheidungen zu treffen!',
    'why.more': 'mehr dazu',
    
    // Contracts
    'contracts.title': 'Verträge analysieren und verstehen',
    'contracts.subtitle': 'Wir analysieren Ihre Verträge aller Art. Das gilt z.B. für nachfolgende Vertragstypen:',
    'contracts.note': 'Die Liste ist nicht vollständig und enthält nur einige Beispiele von Vertragstypen. Wir analysieren sämtliche Verträge, die für eine professionelle Prüfung geeignet sind.',
    'contracts.credit': 'Kreditverträge',
    'contracts.rental': 'Mietverträge',
    'contracts.employment': 'Arbeitsverträge',
    'contracts.purchase': 'Kaufverträge',
    'contracts.insurance': 'Versicherungsverträge',
    'contracts.service': 'Dienstleistungsverträge',
    'contracts.software': 'Software-Lizenzen',
    'contracts.terms': 'Nutzungsbedingungen',
    'contracts.leasing': 'Leasingverträge',
    'contracts.franchise': 'Franchise-Verträge',
    'contracts.partnership': 'Partnerschaftsverträge',
    'contracts.nda': 'NDA',
    'contracts.work': 'Werkverträge',
    'contracts.delivery': 'Lieferverträge',
    
    // About
    'about.title': 'Über CreditCharges',
    'about.description1': 'Hinter CreditCharges steht eine auf Vertragsanalyse spezialisierte Plattform. Die Analysen werden daher mit professioneller Expertise und Vertraulichkeit behandelt.',
    'about.description2.title': 'Professionelle Vertragsanalyse',
    'about.description2': 'CreditCharges wird von einem Team von Experten betreut, die auf Vertragsanalyse und rechtliche Prüfung spezialisiert sind. Unsere Plattform kombiniert KI-gestützte Analyse mit juristischer Expertise, um Ihnen umfassende und zuverlässige Ergebnisse zu liefern.',
    
    // FAQ
  'faq.title': 'Häufig gestellte Fragen',
'faq.subtitle': 'Nachfolgend finden Sie Antworten auf häufig gestellte Fragen zu unseren Leistungen.',
'faq.q1': 'Welche Verträge können Gegenstand einer Analyse sein?',
'faq.a1': 'Gegenwärtig ist die Analyse auf Darlehensverträge beschränkt. Eine Erweiterung auf weitere Vertragsarten, insbesondere Miet-, Arbeits-, Kauf- und Versicherungsverträge sowie Nutzungsbedingungen und sonstige vertragliche Dokumente, ist beabsichtigt.',
 
'faq.q2': 'Wie lange nimmt die Analyse in Anspruch?',
'faq.a2': 'Die Bearbeitungsdauer ist insbesondere vom Umfang und der Komplexität des jeweiligen Vertrags abhängig. Erste Analyseergebnisse werden in der Regel innerhalb von 24 bis 48 Stunden nach Eingang der Unterlagen bereitgestellt.',
 
'faq.q3': 'Welche Möglichkeiten der Finanzierung bestehen?',
'faq.a3': 'Für unsere Leistungen stehen verschiedene Preis- und Nutzungsmodelle zur Verfügung, darunter Einzelanalysen und abonnementbasierte Angebote. Gerne beraten wir Sie individuell zu den für Sie geeigneten Konditionen.',
 
'faq.q4': 'Welche Schritte sind erforderlich, um Verträge analysieren zu lassen?',
'faq.a4': 'Für die Inanspruchnahme der Analyse laden Sie Ihre Vertragsunterlagen bitte über unsere Plattform hoch. Derzeit werden PDF-Dateien bis zu einer Größe von 50 MB unterstützt. Nach erfolgreicher Übermittlung wird der automatisierte Analysevorgang gestartet.',
    // CTA
    'cta.title': 'Vertrag analysieren lassen',
    'cta.subtitle': 'Möchten Sie Ihre Verträge analysieren lassen oder nähere Informationen? Nehmen Sie jetzt unverbindlich Kontakt mit uns auf!',
    'cta.button': 'Jetzt starten',
    
    // Footer
    'footer.copyright': '© 2024 CreditCharges. All rights reserved.',
    
    // Landing Page - Credit Refund
    'landing.serviceName': 'Klagma',
    'landing.tagline': 'Kreditgebühren zurückfordern',
    'landing.nav.whyUs': 'Warum wir?',
    'landing.nav.stats': 'Statistik',
    'landing.nav.howItWorks': 'Wie wir vorgehen',
    'landing.nav.faq': 'FAQ',
    'landing.login': 'Login',
    'landing.getFreeCheck': 'Kostenlose Prüfung',
    'landing.hero.badge': 'Österreich · Kreditgebühren-Rückerstattungsservice',
    'landing.hero.title': 'Haben Sie Anspruch auf <span>Rückerstattung</span>?',
    'landing.hero.reasonTitle': 'Warum sich eine Prüfung Ihres Kreditvertrags lohnt:',
    'landing.hero.reason.p1': 'Aktuelle Entscheidungen des Obersten Gerichtshofs (OGH) haben den Verbraucherschutz in Österreich deutlich gestärkt. Klar ist nun: Banken dürfen nur jene Gebühren verlangen, die durch eine tatsächlich erbrachte und nachvollziehbare Leistung gerechtfertigt sind.',
    'landing.hero.reason.p2': 'In der Praxis zeigt sich jedoch, dass in dem Großteil der Kreditverträge darüber hinausgehende oder intransparente Gebühren verrechnet wurden. Das kann bedeuten, dass Ihnen eine Rückerstattung dieser unrechtmäßigen Kosten zusteht – oft in erheblicher Höhe.',
    'landing.hero.reason.p3': 'Mit uns prüfen Sie einfach und unverbindlich, ob Ihr Kreditvertrag unzulässige Gebühren enthält.',
    'landing.hero.cta': 'Kostenlose Vertragsprüfung starten',
    'landing.hero.disclaimer': 'Keine Verpflichtung · Erste Schätzung kostenlos · 100% DSGVO-konform',
    'landing.hero.hosted': 'Einfach. Transparent. Sicher.',
    'landing.hero.caseLaw': 'Basierend auf aktuellen Gerichtsurteilen!',
    'landing.stats.title': 'Kreditgebühren-Rückerstattungen',
    'landing.stats.demand': 'Jährliches Interesse & Nachfrage',
    'landing.stats.inquiries': 'Anfragen',
    'landing.stats.borrowers.description': 'geprüften Kreditverträgen enthalten intransparente Klauseln oder unzulässige Gebühren!',
    'landing.stats.cases.description': 'der Bearbeitungsgebühren können rückforderbar sein!',
    'landing.stats.period.description': 'rückwirkend – auch bei bereits beendeten Krediten!',
    'landing.stats.demand.description': 'Verbraucherorganisationen melden jedes Jahr Tausende von Anfragen von Kreditnehmern, die überprüfen möchten, ob sie Kreditgebühren zurückfordern können.',
    'landing.howItWorks.title': 'Wie wir vorgehen',
    'landing.howItWorks.lead': 'Kostenlos. Unverbindlich. Sicher.',
    'landing.howItWorks.left.p1': 'Die KI-gestützte Erstprüfung Ihrer Verträge ist für Sie kostenlos, unverbindlich und sicher. Es entstehen erst dann Kosten, wenn Sie sich ausdrücklich dafür entscheiden, Ihre Ansprüche weiterzuverfolgen.',
    'landing.howItWorks.left.p2': 'Sie laden Ihren Kreditvertrag hoch und erhalten eine automatisierte Ersteinschätzung, ob es Anhaltspunkte für unzulässig erhobene Kosten und Gebühren in Ihren Verträgen gibt.',
    'landing.howItWorks.left.p3': 'Die kostenlose Erstprüfung erfolgt mithilfe speziell trainierter KI-Modelle und dient einer formalen Vorprüfung. Sie stellt keine individuelle Rechtsberatung dar.',
    'landing.howItWorks.left.p4': 'Alle weiteren Schritte erfolgen ausschließlich nur mit Ihrer ausdrücklichen Zustimmung.',
    'landing.howItWorks.left.p5': 'Fällt die Ersteinschätzung positiv aus, verfassen wir auf Ihren Wunsch hin und auf Grundlage eines persönlichen Honorangebots ein außergerichtliches Anspruchsschreiben an die Bank.',
    'landing.howItWorks.left.p6': 'Ihre Daten und Dokumente sind bei uns in sicheren Händen. Wir verwenden modernste Sicherheitsstandards und verschlüsselte Kommunikationskanäle, um Ihre Daten jederzeit bestmöglich zu schützen.',
    'landing.howItWorks.cta': 'KOSTENLOSE ERSTPRÜFUNG STARTEN',
    'landing.howItWorks.detail1.title': '1. Kostenlose Registrierung',
    'landing.howItWorks.detail1.body': 'Registrieren Sie sich mit Ihrer E-Mail-Adresse, damit Sie immer wieder auf Ihre Unterlagen zugreifen können. Außerdem können wir Sie so über das Ergebnis Ihrer Erstprüfung informieren.',
    'landing.howItWorks.detail2.title': '2. Vertrag unverbindlich hochladen',
    'landing.howItWorks.detail2.body': 'Laden Sie Ihren Kreditvertrag hoch (z. B. als PDF). Ihre Dokumente werden verschlüsselt verarbeitet und ausschließlich für die automatisierte Vertragsanalyse verwendet.',
    'landing.howItWorks.detail3.title': '3. Automatisierte Erstprüfung',
    'landing.howItWorks.detail3.body': 'Ihr Vertrag wird mithilfe einer eigens trainierten, automatisierten KI ausgewertet. Dieser Schritt dient der strukturierten Vorprüfung und ersetzt keine individuelle Rechtsberatung.',
    'landing.howItWorks.detail4.title': '4. Ergebnis einsehen',
    'landing.howItWorks.detail4.body': 'Sie erhalten eine Ersteinschätzung, ob Ihr Vertrag möglicherweise rückforderungsfähige Kosten und Gebühren enthält – inklusive einer ersten Schätzung über die Höhe Ihrer Rückforderung. Das Ergebnis ist unverbindlich und stellt keine Garantie für eine Rückerstattung oder einen bestimmten rechtlichen Ausgang dar.',
    'landing.howItWorks.detail5.title': '5. Entscheidung treffen',
    'landing.howItWorks.detail5.body': 'Danach entscheiden Sie, wie es weitergeht: Sie können eine weiterführende rechtliche Prüfung durch unsere Kanzlei anfordern oder Ihre hochgeladenen Daten dauerhaft löschen. Entscheiden Sie sich für eine Weiterverfolgung, stellen wir Ihnen ein individuelles Angebot zusammen.',
    'landing.howItWorks.footnote': '*Das Ergebnis ist unverbindlich und stellt keine Garantie für eine Rückerstattung oder einen bestimmten rechtlichen Ausgang dar.',
    'landing.whyUs.title': 'Warum uns wählen?',
    'landing.whyUs.analysis.title': 'Spezialisierte Vertragsanalyse',
    'landing.whyUs.analysis.description': 'Automatisierte Vorprüfung für österreichische Kreditverträge, die verdächtige Gebührenklauseln und potenzielle Rückerstattungspositionen hervorhebt.',
    'landing.whyUs.estimation.title': 'Rechtlich fundierte Schätzung',
    'landing.whyUs.estimation.description': 'Ihre Schätzung kann sich auf aktuelle OGH-Entscheidungen und Verbraucherschutzpraxis beziehen, um Ergebnisse transparent und vertrauenswürdig zu machen.',
    'landing.whyUs.gdpr.title': 'DSGVO-first & transparent',
    'landing.whyUs.gdpr.description': 'Die Datenverarbeitung ist vollständig transparent und entspricht der EU-DSGVO. Klären Sie, wo Ihre Daten gespeichert werden und wer Zugriff auf Ihre Dokumente hat.',
    'landing.gdpr.title': 'DSGVO & rechtliche Transparenz',
    'landing.gdpr.subtitle': 'Kurze Zusammenfassungskarte für Datenschutz- und Rechtsinformationen.',
    'landing.gdpr.bullet1': 'Daten werden in der EU gespeichert',
    'landing.gdpr.bullet2': 'Kein Verkauf personenbezogener Daten',
    'landing.gdpr.bullet3': 'Löschung auf Anfrage',
    'landing.gdpr.bullet4': 'Verschlüsselung bei der Übertragung',
    'landing.gdpr.readMore': 'Datenschutzzusammenfassung lesen',
    'landing.footer.disclaimer': '© {year} KreditCheck – Diese Seite ersetzt keine Rechtsberatung. Bitte konsultieren Sie einen Anwalt für eine verbindliche rechtliche Bewertung.',
    'landing.footer.gdpr': 'Datenschutz',
    'landing.footer.impressum': 'Impressum',

    // Common (all pages)
    'common.backToHome': 'Zur Startseite',
    'common.logout': 'Abmelden',
    'common.dashboard': 'Dashboard',
    'common.emailNotVerified': 'E-Mail nicht bestätigt',
    // Verify email
    'verifyEmail.goToDashboard': 'Zum Dashboard',
    'verifyEmail.tokenMissing': 'Verifizierungstoken fehlt',
    'verifyEmail.success': 'E-Mail erfolgreich bestätigt!',
    'verifyEmail.alreadyVerified': 'Ihre E-Mail ist bereits bestätigt!',
    'verifyEmail.failed': 'Verifizierung fehlgeschlagen',
    'verifyEmail.sent': 'Verifizierungs-E-Mail gesendet! Bitte prüfen Sie Ihren Posteingang.',
    'verifyEmail.resendFailed': 'Verifizierungs-E-Mail konnte nicht erneut gesendet werden.',
    'verifyEmail.verifying': 'E-Mail wird bestätigt...',
    'verifyEmail.verifyingWait': 'Bitte warten Sie, wir bestätigen Ihre E-Mail-Adresse.',
    'verifyEmail.emailVerified': 'E-Mail bestätigt!',
    'verifyEmail.linkExpired': 'Verifizierungslink abgelaufen',
    'verifyEmail.resend': 'Verifizierungs-E-Mail erneut senden',
    'verifyEmail.sending': 'Wird gesendet...',
    'verifyEmail.failedTitle': 'Verifizierung fehlgeschlagen',
    'verifyEmail.goToLogin': 'Zum Login',
    'auth.createAccount': 'Konto erstellen',

    // User page – next steps / disclaimer
    'userPage.nextSteps.title': 'Was passiert im nächsten Schritt?',
    'userPage.nextSteps.p1': 'Nach dem Klick auf „Mit Anwalt fortfahren" erhalten Sie ein persönliches Angebot für Ihre weitere anwaltliche Betreuung.',
    'userPage.nextSteps.p2': 'Sie können dieses Angebot in Ruhe prüfen und frei entscheiden, ob Sie es annehmen möchten oder nicht. Kosten entstehen für Sie erst dann, wenn Sie das separat übermittelte Angebot ausdrücklich annehmen.',
    'userPage.nextSteps.subtitle': 'Sie entscheiden sich gegen eine Weiterverfolgung? Kein Problem!',
    'userPage.nextSteps.p3': 'Klicken Sie auf „Abbrechen und Vertrag löschen" – Ihre hochgeladenen Dokumente werden dauerhaft gelöscht.',

    // Landing – hero steps (DE-only block, but keys for EN if shown)
    'landing.hero.steps.title': 'So einfach geht\'s:',
    'landing.hero.steps.step1': 'Kostenlos Registrieren',
    'landing.hero.steps.step2': 'Kreditunterlagen unverbindlich hochladen',
    'landing.hero.steps.step3a': 'Kostenlose Ersteinschätzung in weniger als 24 Stunden',
    'landing.hero.steps.step3b1': '100% vertraulich - Keine Information an Ihre Bank',
    'landing.hero.steps.step3b2': 'Unverbindlich & ohne Risiko',
    'landing.hero.steps.step3b3': 'DSGVO-konform',
    'landing.hero.steps.step4': 'Auf Wunsch - Beauftragung Anwalt mit der Geltendmachung der Ansprüche',
    'landing.howItWorks.pillFree': 'Kostenlos',
    'landing.howItWorks.pillNonBinding': 'Unverbindlich',
    'landing.howItWorks.pillSecure': 'Sicher',
    'landing.stats.value1': '9 von 10',
    'landing.stats.value2': 'Bis zu 100 %',
    'landing.stats.value3': 'Bis zu 30 Jahre',

    // User page – upload & jobs
    'userPage.documentsTitle': 'Welche Dokumente sind hilfreich?',
    'userPage.documentsIntro': 'Für eine möglichst genaue Einschätzung Ihres Anspruchs laden Sie bitte folgende Unterlagen soweit verfügbar hoch:',
    'userPage.documentsNote': 'Optionale Dokumente können Sie gerne auch später noch ergänzen.',
    'userPage.noFilesSelected': 'Noch keine Dateien ausgewählt. Wählen Sie mindestens ein Dokument aus, um die Analyse zu starten.',
    'userPage.consentLabel': 'Ich stimme der automatisierten Analyse meiner hochgeladenen Dokumente gemäß',
    'userPage.consentLink': 'Datenschutzerklärung zu.',
    'userPage.uploadDisclaimer': 'Mit einem Klick auf „Upload & Analyse starten" werden Ihre Dokumente verschlüsselt übertragen und eine neue Analyse gestartet.',
    'userPage.uploadButton': 'Upload & Analyse starten',
    'userPage.uploading': 'Wird hochgeladen...',
    'userPage.jobsTitle': 'Kreditprüfungen',
    'userPage.jobCountOne': 'Job',
    'userPage.jobCountMany': 'Jobs',
    'userPage.loadingJobs': 'Jobs werden geladen...',
    'userPage.noJobs': 'Noch keine Jobs',
    'userPage.noJobsHint': 'Laden Sie Ihren ersten Vertrag hoch, um hier den Fortschritt der Analyse zu sehen.',
    'userPage.createdAt': 'Erstellt:',
    'userPage.statusDone': 'Fertig',
    'userPage.statusLawyerNotified': 'Anwalt benachrichtigt',
    'userPage.statusProcessing': 'In Analyse',
    'userPage.statusError': 'Fehler',
    'userPage.statusQueued': 'In Warteschlange',
    'userPage.assertClaims': 'Mit Anwalt fortfahren',
    'userPage.notifyLawyer': 'Benachrichtige Anwalt...',
    'userPage.notifyLawyerCooldown': 'Anwalt bereits benachrichtigt (Warten {{h}}h)',
    'userPage.deleteContract': 'Vertrag löschen',
    'userPage.deleteConfirm': 'Möchten Sie diesen Vertrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'userPage.deleteSuccess': 'Vertrag wurde erfolgreich gelöscht.',
    'userPage.deleteError': 'Fehler beim Löschen des Vertrags. Bitte versuchen Sie es erneut.',
    'userPage.criteriaLoadError': 'Bewertung liegt vor; Anzeige der Kriterien konnte nicht geladen werden. Sie können dennoch mit dem Anwalt fortfahren.',
    'userPage.analysisRunning': 'Analyse läuft',
    'userPage.analysisRunningDesc': 'Bitte warten Sie, während wir Ihre Dokumente analysieren. Dies kann einige Minuten dauern. Sie erhalten eine E-Mail, sobald die Analyse abgeschlossen ist.',
    'userPage.analysisFailed': 'Analyse fehlgeschlagen',
    'userPage.analysisFailedDesc': 'Die Analyse konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support, falls das Problem weiterhin besteht.',
    'userPage.retryButton': 'Erneut versuchen',
    'userPage.emailNote': 'Sobald eine Analyse abgeschlossen ist, erhalten Sie zusätzlich eine E-Mail mit dem Ergebnis.',

    // User page – upload area & document list
    'userPage.pageTitle': 'Kreditunterlagen hochladen',
    'userPage.pageIntro': 'Laden Sie Ihren Kreditvertrag und sonstige relevante Unterlagen, z. B. AGB oder Beilagen zum Kreditvertrag hoch. Je vollständiger Ihre Unterlagen, umso besser können wir eine erste Einschätzung abgeben.',
    'userPage.badgeGdpr': 'DSGVO-konform',
    'userPage.badgeResult': 'Ergebnis ≤ 24h',
    'userPage.dropzoneTitle': 'Dateien hier ablegen oder auswählen',
    'userPage.allowedFormats': 'Erlaubte Formate: PDF, ZIP',
    'userPage.maxSize': 'Max. 50 MB pro Hochladen',
    'userPage.dropzoneHint': 'Klicken Sie zum Auswählen oder ziehen Sie Ihre Daten hierher',
    'userPage.multipleDocs': 'Mehrere Dokumente möglich (z. B. Vertrag, AGB, Nachträge, Konditionen)',
    'userPage.bestResultTitle': 'So erzielen Sie das beste Ergebnis',
    'userPage.bestResult1': 'Laden Sie bitte immer nur einen Kredit gemeinsam mit den verfügbaren Anhängen auf einmal hoch.',
    'userPage.bestResult2': 'Dokumente möglichst vollständig und gut lesbar scannen',
    'userPage.bestResult3': 'Ihre Daten werden verschlüsselt gespeichert',
    'userPage.bestResult4': 'Sie können später weitere Kredite hinzufügen. Jeder Upload wird separat analysiert.',
    'userPage.selectedFiles': 'Ausgewählte Dateien',
    'userPage.docList1': 'Kreditvertrag inkl. etwaiger Nachträge (*verpflichtend)',
    'userPage.docList2': 'AGB (optional)',
    'userPage.docList3': 'Konditionenblatt (optional)',
    'userPage.docList4': 'Zins- & Spesenübersicht (optional)',
    'userPage.docList5': 'Gebührenabrechnungen oder Kontoauszüge (optional)',
    'userPage.docList6': 'ggf. Schriftverkehr mit der Bank (optional)',
    'userPage.errorInvalidType': 'Ungültige Dateitypen. Nur PDF und ZIP sind erlaubt.',
    'userPage.errorFileTooBig': 'Dateien zu groß. Maximale Größe: 50 MB pro Datei.',
    'userPage.errorMaxFiles': 'Maximal {{max}} Dateien pro Analyse. Es wurden nur die ersten {{max}} übernommen.',
    'userPage.errorVerifyEmail': 'Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse, bevor Sie Dokumente hochladen.',
    'userPage.errorLoadJobs': 'Jobs konnten nicht geladen werden. Prüfen Sie die Verbindung.',
    'userPage.errorUploadFailed': 'Upload fehlgeschlagen',
    'userPage.errorUnknown': 'Unbekannter Fehler',
    'userPage.errorUploadFile': 'Fehler beim Hochladen von {{name}}',
    'userPage.toastVerificationSent': 'Bestätigungs-E-Mail wurde gesendet. Bitte prüfen Sie Ihren Posteingang.',
    'userPage.toastVerificationFailed': 'E-Mail zur Verifizierung konnte nicht erneut gesendet werden.',
    'userPage.toastLawyerNotified': 'Anwalt wurde erfolgreich benachrichtigt. Sie werden in Kürze kontaktiert.',
    'userPage.toastLawyerNotifyError': 'Fehler beim Benachrichtigen des Anwalts. Bitte versuchen Sie es erneut.',
    'userPage.eligibilityNoClaim': 'Kein Anspruch auf Rückerstattung',
    'userPage.eligibilityReason': 'Grund: Bei mindestens einer der folgenden Fragen war die Antwort „Nein“:',
    'userPage.eligibilityManualReview': 'Manuelle Anwaltsprüfung erforderlich (Bewertung anhand der Kriterien nicht eindeutig möglich)',
    'userPage.eligibilityPossibleClaim': 'Möglicher Anspruch auf Rückerstattung – nächster Schritt: Anwalt benachrichtigen',
    'userPage.eligibilityHeading': 'Anspruch auf Rückerstattung',
    'userPage.resetSelection': 'Auswahl zurücksetzen',

    // Partner section
    'partner.title': 'Jetzt unrechtmäßige Kreditgebühren zurückfordern!',
    'partner.subtitle': 'Haben auch Sie zu viel für Ihren Kredit bezahlt?',
    'partner.p1': 'Prüfen Sie anonym, rasch und kostenlos, ob Ihre Finanzierung überhöhte Kosten oder Gebühren enthält!',
    'partner.p2': 'Nehmen Sie sich jetzt 5 Minuten Zeit. Unverbindlich & Ohne Vorabkosten!',
    'partner.cta': 'Erfahren Sie mehr!',
    'partner.stat1': '9 von 10 geprüften Kreditverträgen enthalten intransparente Klauseln oder unzulässige Gebühren!',
    'partner.stat2': 'Bis zu 100 % der Bearbeitungsgebühren können rückforderbar sein!',
    'partner.stat3': 'Bis zu 30 Jahre rückwirkend – auch bei bereits beendeten Krediten!',

    // Login
    'login.emailRequired': 'E-Mail ist erforderlich',
    'login.emailInvalid': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    'login.passwordRequired': 'Passwort ist erforderlich',
    'login.passwordMinLength': 'Passwort muss mindestens 8 Zeichen haben',
    'login.passwordUppercase': 'Passwort muss mindestens einen Großbuchstaben enthalten',
    'login.passwordNumber': 'Passwort muss mindestens eine Zahl enthalten',
    'login.passwordSpecial': 'Passwort muss mindestens ein Sonderzeichen enthalten',
    'login.title': 'Login',
    'login.subtitle': 'Melden Sie sich an, um fortzufahren',
    'login.emailLabel': 'E-Mail',
    'login.passwordLabel': 'Passwort',
    'login.loggingIn': 'Wird angemeldet…',
    'login.submit': 'Login',
    'login.noAccount': 'Noch kein Konto?',
    'login.registerHere': 'Hier registrieren',
    'login.errorFailed': 'Anmeldung fehlgeschlagen',
    'login.invalidCredentials': 'E-Mail oder Passwort ist ungültig.',
    'login.apiEmailPasswordRequired': 'E-Mail und Passwort sind erforderlich',
    'login.apiTenantNotFound': 'Mandant nicht gefunden',
    'login.apiInvalidSubdomain': 'Ungültige Subdomain',
    'login.networkHint':
      ' Versuchen Sie einen Hard-Refresh (Strg+Umschalt+R), um die neueste App zu laden. Wenn das Problem bleibt, ist die API möglicherweise nicht erreichbar oder CORS blockiert die Antwort.',
    'login.buildIdTitle': 'Build-Kennung für den Support',

    // Register
    'register.subtitle': 'Registrieren Sie sich für Ihre kostenlose Vertragsprüfung',
    'register.firstName': 'Vorname',
    'register.lastName': 'Nachname',
    'register.emailLabel': 'E-Mail',
    'register.passwordLabel': 'Passwort',
    'register.confirmPassword': 'Passwort bestätigen',
    'register.passwordRequirementsHint':
      'Passwort-Anforderungen: mindestens 8 Zeichen, 1 Großbuchstabe, 1 Zahl, 1 Sonderzeichen',
    'register.registering': 'Wird registriert…',
    'register.alreadyHaveAccount': 'Bereits ein Konto?',
    'register.loginHere': 'Zum Login',
    'register.firstNameRequired': 'Vorname ist erforderlich',
    'register.lastNameRequired': 'Nachname ist erforderlich',
    'register.confirmPasswordRequired': 'Bitte bestätigen Sie Ihr Passwort',
    'register.passwordsMismatch': 'Passwörter stimmen nicht überein',
    'register.captchaFailed': 'CAPTCHA-Überprüfung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    'register.captchaRequired': 'Bitte schließen Sie die CAPTCHA-Überprüfung ab',
    'register.successToast':
      'Registrierung erfolgreich! Bitte bestätigen Sie Ihre E-Mail-Adresse, bevor Sie sich anmelden.',
    'register.errorFailed': 'Registrierung fehlgeschlagen',

    // Dashboard
    'dashboard.errorLoadJobs': 'Jobs konnten nicht geladen werden. Prüfen Sie die Verbindung.',
    'dashboard.fileTypeError': 'Nur PDF- oder ZIP-Dateien erlaubt',
    'dashboard.fileSizeError': 'Dateigröße maximal 50MB',
    'dashboard.selectFile': 'Bitte wählen Sie eine Datei',
    'dashboard.uploadFailed': 'Upload fehlgeschlagen',

    // JobDetail
    'jobDetail.notFound': 'Job nicht gefunden',
    'jobDetail.title': 'Job-Details',
    'jobDetail.jobId': 'Job-ID:',
    'jobDetail.loading': 'Job-Details werden geladen...',
    'jobDetail.backToDashboard': 'Zurück zum Dashboard',
    'jobDetail.claimOutcome': 'Ergebnis der Prüfung',
    'jobDetail.noClaim': 'Kein Anspruch',
    'jobDetail.reason': 'Grund: Bei mindestens einer der folgenden Fragen war die Antwort „Nein“:',
    'jobDetail.manualReview': 'Manuelle Rechtsprüfung empfohlen (Ersteinschätzung nicht abschließend).',
    'jobDetail.possibleClaim': 'Möglicher Anspruch auf Rückerstattung',
  },
  en: {
    // Header
    'header.login': 'Login',
    'header.start': 'Get Started',

    // Cookie banner (storage notice — no third-party marketing/analytics scripts in app)
    'cookieBanner.ariaLabel': 'Cookies and local storage notice',
    'cookieBanner.lead':
      'We use browser storage (e.g. for sign-in and language). We do not currently load third-party marketing or analytics cookies. Please see our',
    'cookieBanner.privacy': 'privacy policy',
    'cookieBanner.details': 'Details',
    'cookieBanner.acknowledge': 'OK',
    'cookieBanner.settingsTitle': 'Cookies and local storage',
    'cookieBanner.settingsIntro':
      'Under GDPR, local storage and session storage also count as technologies you should be informed about. The following data may be stored in your browser:',
    'cookieBanner.storageAuthTitle': 'Sign-in',
    'cookieBanner.storageAuthDesc':
      'Keys “token” and “user” (localStorage) for your session after login.',
    'cookieBanner.storageLanguageTitle': 'Language',
    'cookieBanner.storageLanguageDesc':
      'Key “language” (localStorage) for your selected UI language.',
    'cookieBanner.storageConsentTitle': 'Cookie notice',
    'cookieBanner.storageConsentDesc':
      'Key “klagma_cookie_prefs” (localStorage) to record that you have seen this notice.',
    'cookieBanner.storageSessionTitle': 'Session (technical)',
    'cookieBanner.storageSessionDesc':
      'Short-lived sessionStorage entries to avoid redundant requests while you use the app—not used for advertising profiles.',
    'cookieBanner.noThirdParty':
      'We do not currently embed third-party tracking or marketing tools (e.g. Meta Pixel, Google Analytics) in this site.',
    'cookieBanner.cancel': 'Close',
    'cookieBanner.reopenLink': 'Cookie settings',

    // Hero
    'hero.title': 'Contract Analysis of\nOnline Contracts',
    'hero.subtitle': 'A service for professional contract review',
    'hero.cta': 'Analyze Now',
    
    // Media
    'media.title': 'Known from the Media',
    
    // Service
    'service.title': 'Lost money on online contracts?',
    'service.description': 'We support you in analyzing your contracts. Losses due to unfavorable contract clauses can generally be identified and avoided. With our professional contract analysis, you can understand and protect your contracts. We have already supported many companies and individuals in analyzing their contracts.',
    'service.discrete': 'Discrete',
    'service.personal': 'Personal',
    'service.efficient': 'Efficient',
    'service.reliable': 'Reliable',
    
    // Steps
    'steps.title': 'Analyze Contracts',
    'steps.subtitle': 'Simply, discretely and quickly register – we analyze your contracts professionally. With these three steps, you secure the chance to understand your contracts:',
    'steps.contact.title': 'Contact',
    'steps.contact.description': 'You contact us via the contact form, by phone or email and send us your contract for analysis. We will reliably get back to you!',
    'steps.analysis.title': 'Analysis',
    'steps.analysis.description': 'We analyze your contract, identify critical clauses and show you potential risks. As part of a free initial assessment, you will learn about the most important findings.',
    'steps.report.title': 'Detailed Report',
    'steps.report.description': 'You will receive a comprehensive analysis report with all important findings, risk assessments and recommendations for action. We support you in interpreting the results.',
    
    // Why
    'why.title': 'Why should contracts be analyzed?',
    'why.description1': 'Many contracts contain complex clauses that are difficult for laypeople to understand. Hidden costs, unfavorable conditions or legal pitfalls can lead to financial losses. A professional contract analysis helps you identify and avoid these risks.',
    'why.description2': 'With our AI-supported analysis and legal expertise, we stand up for your rights and help you make informed decisions!',
    'why.more': 'learn more',
    
    // Contracts
    'contracts.title': 'Analyze and understand contracts',
    'contracts.subtitle': 'We analyze all types of your contracts. This applies, for example, to the following contract types:',
    'contracts.note': 'The list is not complete and contains only some examples of contract types. We analyze all contracts that are suitable for professional review.',
    'contracts.credit': 'Credit Contracts',
    'contracts.rental': 'Rental Contracts',
    'contracts.employment': 'Employment Contracts',
    'contracts.purchase': 'Purchase Contracts',
    'contracts.insurance': 'Insurance Contracts',
    'contracts.service': 'Service Contracts',
    'contracts.software': 'Software Licenses',
    'contracts.terms': 'Terms of Use',
    'contracts.leasing': 'Leasing Contracts',
    'contracts.franchise': 'Franchise Contracts',
    'contracts.partnership': 'Partnership Contracts',
    'contracts.nda': 'NDA',
    'contracts.work': 'Work Contracts',
    'contracts.delivery': 'Delivery Contracts',
    
    // About
    'about.title': 'About CreditCharges',
    'about.description1': 'Behind CreditCharges is a platform specialized in contract analysis. The analyses are therefore handled with professional expertise and confidentiality.',
    'about.description2.title': 'Professional Contract Analysis',
    'about.description2': 'CreditCharges is managed by a team of experts specialized in contract analysis and legal review. Our platform combines AI-supported analysis with legal expertise to provide you with comprehensive and reliable results.',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Find answers to commonly asked questions about our service.',
    'faq.q1': 'Which contracts can be analyzed?',
    'faq.a1': 'Right now we only analyze credit agreements. Other contract types—such as rental, employment, purchase and insurance contracts, terms of use, and more—are planned for future updates.',
    'faq.q2': 'How long does an analysis take?',
    'faq.a2': 'The analysis time depends on the complexity and length of the contract. Usually you will receive initial results within 24-48 hours after upload.',
    'faq.q3': 'How can I finance the analysis?',
    'faq.a3': 'We offer various pricing plans for individual analyses and subscriptions. Contact us for individual advice on financing options.',
    'faq.q4': 'What do I need to do to have my contracts analyzed?',
    'faq.a4': 'Best upload your contracts directly via our platform. We support PDF files up to 50MB. After upload, the automatic analysis starts.',
    
    // CTA
    'cta.title': 'Have contract analyzed',
    'cta.subtitle': 'Would you like to have your contracts analyzed or need more information? Contact us now without obligation!',
    'cta.button': 'Get Started',
    
    // Footer
    'footer.copyright': '© 2024 CreditCharges. All rights reserved.',
    
    // Landing Page - Credit Refund
    'landing.serviceName': 'Klagma',
    'landing.tagline': 'Refund your hidden credit fees',
    'landing.nav.howItWorks': 'How it works',
    'landing.nav.whyUs': 'Why choose us',
    'landing.nav.stats': 'Statistics',
    'landing.nav.faq': 'FAQ',
    'landing.login': 'Login',
    'landing.getFreeCheck': 'Get free check',
    'landing.hero.badge': 'Austria · Credit fee refund service',
    'landing.hero.title': 'Are you entitled to a <span>refund</span>?',
    'landing.hero.reasonTitle': 'Why reviewing your credit agreement pays off:',
    'landing.hero.reason.p1': 'Recent Austrian Supreme Court rulings have significantly strengthened consumer protection. It is now clear that banks may only charge fees that are justified by a concrete, verifiable service.',
    'landing.hero.reason.p2': 'In practice, many credit agreements include additional or opaque fees. That can mean you are entitled to a refund of unlawful costs—often substantial.',
    'landing.hero.reason.p3': 'With us, you can quickly and non-bindingly check whether your contract contains impermissible fees.',
    'landing.hero.cta': 'Start free contract check',
    'landing.hero.disclaimer': 'No commitment · First estimation for free · 100% GDPR-compliant',
    'landing.hero.hosted': 'Hosted in EU',
    'landing.hero.caseLaw': 'Based on Austrian case law',
    'landing.stats.title': 'Credit Fee Refunds',
    'landing.stats.demand': 'Annual Interest & Demand',
    'landing.stats.inquiries': 'of inquiries',
    'landing.stats.borrowers.description': 'Borrowers who have already pursued credit fee refunds with the help of consumer organizations such as VSV.',
    'landing.stats.cases.description': 'Number of refund cases handled in a single legal action involving an Austrian bank (e.g. BAWAG disputes reported by consumer groups).',
    'landing.stats.period.description': 'Supreme Court decisions have made it possible, in many cases, to claim unlawful credit fees from loans concluded up to 30 years in the past.',
    'landing.stats.demand.description': 'Consumer organizations report thousands of inquiries every year from borrowers who want to check whether they can reclaim credit fees.',
    'landing.howItWorks.title': 'How we proceed',
    'landing.howItWorks.lead': 'Free. Non-binding. Secure.',
    'landing.howItWorks.left.p1': 'The AI-supported initial review of your contracts is free, non-binding, and secure. Costs only arise if you explicitly decide to pursue your claims.',
    'landing.howItWorks.left.p2': 'You upload your loan contract and receive an automated initial assessment of whether there are indications of unlawful fees or costs.',
    'landing.howItWorks.left.p3': 'The free initial review uses specially trained AI models and serves as a formal pre-check. It does not constitute individual legal advice.',
    'landing.howItWorks.left.p4': 'All further steps take place only with your explicit consent.',
    'landing.howItWorks.left.p5': 'If the initial assessment is positive, we can draft an out-of-court claim letter to the bank based on a personal fee offer at your request.',
    'landing.howItWorks.left.p6': 'Your data and documents are in safe hands. We use modern security standards and encrypted communication channels to protect your data at all times.',
    'landing.howItWorks.cta': 'START FREE INITIAL REVIEW',
    'landing.howItWorks.detail1.title': '1. Free registration',
    'landing.howItWorks.detail1.body': 'Register with your email address so you can access your documents later. We can also inform you about the result of your initial review.',
    'landing.howItWorks.detail2.title': '2. Upload contract without obligation',
    'landing.howItWorks.detail2.body': 'Upload your loan contract (e.g., as a PDF). Your documents are processed with encryption and used only for the automated analysis.',
    'landing.howItWorks.detail3.title': '3. Automated initial review',
    'landing.howItWorks.detail3.body': 'Your contract is evaluated using a dedicated, automated AI. This step provides a structured pre-check and does not replace individual legal advice.',
    'landing.howItWorks.detail4.title': '4. View results',
    'landing.howItWorks.detail4.body': 'You receive an initial assessment of whether your contract may contain refundable fees and costs, including a first estimate of the refund amount. The result is non-binding and does not guarantee a refund or a specific legal outcome.',
    'landing.howItWorks.detail5.title': '5. Make a decision',
    'landing.howItWorks.detail5.body': 'Then you decide how to proceed: request further legal review by our law firm or permanently delete your uploaded data. If you choose to continue, we will prepare an individual offer.',
    'landing.howItWorks.footnote': '*The result is non-binding and does not guarantee a refund or a specific legal outcome.',
    'landing.whyUs.title': 'Why choose us?',
    'landing.whyUs.analysis.title': 'Specialised contract analysis',
    'landing.whyUs.analysis.description': 'Automated pre-check tailored to Austrian credit contracts, highlighting suspicious fee clauses and potential refund positions.',
    'landing.whyUs.estimation.title': 'Legally grounded estimation',
    'landing.whyUs.estimation.description': 'Your estimation can reference current OGH decisions and consumer protection practice to make results transparent and trustworthy.',
    'landing.whyUs.gdpr.title': 'GDPR-first & transparent',
    'landing.whyUs.gdpr.description': 'Data processing is fully transparent and compliant with EU GDPR. Clarify where your data is stored and who can access your documents.',
    'landing.gdpr.title': 'GDPR & legal transparency',
    'landing.gdpr.subtitle': 'Short summary card for privacy & legal info.',
    'landing.gdpr.bullet1': 'Data stored in the EU',
    'landing.gdpr.bullet2': 'No sale of personal data',
    'landing.gdpr.bullet3': 'Deletion on request',
    'landing.gdpr.bullet4': 'Encryption in transit',
    'landing.gdpr.readMore': 'Read data protection summary',
    'landing.footer.disclaimer': '© {year} KreditCheck – This page does not replace legal advice. Please consult a lawyer for binding legal assessment.',
    'landing.footer.gdpr': 'GDPR / Datenschutz',
    'landing.footer.impressum': 'Impressum',

    // Common (all pages)
    'common.backToHome': 'Back to home',
    'common.logout': 'Logout',
    'common.dashboard': 'Dashboard',
    'common.emailNotVerified': 'Email not verified',
    // Verify email
    'verifyEmail.goToDashboard': 'Go to Dashboard',
    'verifyEmail.tokenMissing': 'Verification token is missing',
    'verifyEmail.success': 'Email verified successfully!',
    'verifyEmail.alreadyVerified': 'Your email is already verified!',
    'verifyEmail.failed': 'Verification failed',
    'verifyEmail.sent': 'Verification email sent! Please check your inbox.',
    'verifyEmail.resendFailed': 'Failed to resend verification email.',
    'verifyEmail.verifying': 'Verifying your email...',
    'verifyEmail.verifyingWait': 'Please wait while we verify your email address.',
    'verifyEmail.emailVerified': 'Email Verified!',
    'verifyEmail.linkExpired': 'Verification Link Expired',
    'verifyEmail.resend': 'Resend Verification Email',
    'verifyEmail.sending': 'Sending...',
    'verifyEmail.failedTitle': 'Verification Failed',
    'verifyEmail.goToLogin': 'Go to Login',
    'auth.createAccount': 'Create Account',

    // User page – next steps / disclaimer
    'userPage.nextSteps.title': 'What happens in the next step?',
    'userPage.nextSteps.p1': 'After clicking "Assert claims for a fee", you will receive a personal offer for your further legal support.',
    'userPage.nextSteps.p2': 'You can review this offer at your leisure and freely decide whether you want to accept it or not. Costs will only arise for you if you explicitly accept the separately transmitted offer.',
    'userPage.nextSteps.subtitle': 'You decide against further pursuit? No problem!',
    'userPage.nextSteps.p3': 'Click "Cancel and delete contract" – your uploaded documents will be permanently deleted.',

    // Landing – hero steps
    'landing.hero.steps.title': 'How it works:',
    'landing.hero.steps.step1': 'Free registration',
    'landing.hero.steps.step2': 'Upload credit documents without obligation',
    'landing.hero.steps.step3a': 'Free initial assessment in less than 24 hours',
    'landing.hero.steps.step3b1': '100% confidential – no information shared with your bank',
    'landing.hero.steps.step3b2': 'Non-binding & no risk',
    'landing.hero.steps.step3b3': 'GDPR-compliant',
    'landing.hero.steps.step4': 'On request – instruct a lawyer to assert your claims',
    'landing.howItWorks.pillFree': 'Free',
    'landing.howItWorks.pillNonBinding': 'Non-binding',
    'landing.howItWorks.pillSecure': 'Secure',
    'landing.stats.value1': '9 out of 10',
    'landing.stats.value2': 'Up to 100%',
    'landing.stats.value3': 'Up to 30 years',

    // User page – upload & jobs
    'userPage.documentsTitle': 'Which documents are helpful?',
    'userPage.documentsIntro': 'For the most accurate assessment of your claim, please upload the following documents if available:',
    'userPage.documentsNote': 'You can add optional documents later.',
    'userPage.noFilesSelected': 'No files selected yet. Choose at least one document to start the analysis.',
    'userPage.consentLabel': 'I agree to the automated analysis of my uploaded documents in accordance with the',
    'userPage.consentLink': 'privacy policy.',
    'userPage.uploadDisclaimer': 'By clicking "Start upload & analysis", your documents will be transmitted encrypted and a new analysis will be started.',
    'userPage.uploadButton': 'Start upload & analysis',
    'userPage.uploading': 'Uploading...',
    'userPage.jobsTitle': 'Credit checks',
    'userPage.jobCountOne': 'Job',
    'userPage.jobCountMany': 'Jobs',
    'userPage.loadingJobs': 'Loading jobs...',
    'userPage.noJobs': 'No jobs yet',
    'userPage.noJobsHint': 'Upload your first contract to see the analysis progress here.',
    'userPage.createdAt': 'Created:',
    'userPage.statusDone': 'Done',
    'userPage.statusLawyerNotified': 'Lawyer notified',
    'userPage.statusProcessing': 'In analysis',
    'userPage.statusError': 'Error',
    'userPage.statusQueued': 'Queued',
    'userPage.assertClaims': 'Continue with lawyer',
    'userPage.notifyLawyer': 'Notifying lawyer...',
    'userPage.notifyLawyerCooldown': 'Lawyer already notified (wait {{h}}h)',
    'userPage.deleteContract': 'Delete contract',
    'userPage.deleteConfirm': 'Do you really want to delete this contract? This action cannot be undone.',
    'userPage.deleteSuccess': 'Contract was successfully deleted.',
    'userPage.deleteError': 'Error deleting contract. Please try again.',
    'userPage.criteriaLoadError': 'Assessment is available; criteria display could not be loaded. You can still proceed with the lawyer.',
    'userPage.analysisRunning': 'Analysis in progress',
    'userPage.analysisRunningDesc': 'Please wait while we analyse your documents. This may take a few minutes. You will receive an email when the analysis is complete.',
    'userPage.analysisFailed': 'Analysis failed',
    'userPage.analysisFailedDesc': 'The analysis could not be completed. Please try again or contact support if the problem persists.',
    'userPage.retryButton': 'Try again',
    'userPage.emailNote': 'Once an analysis is complete, you will also receive an email with the outcome.',

    // User page – upload area & document list
    'userPage.pageTitle': 'Upload credit documents',
    'userPage.pageIntro': 'Upload your credit contract and any other relevant documents, e.g. terms and conditions or credit contract annexes. The more complete your documents, the better we can provide an initial assessment.',
    'userPage.badgeGdpr': 'GDPR-compliant',
    'userPage.badgeResult': 'Result within 24h',
    'userPage.dropzoneTitle': 'Drop files here or select',
    'userPage.allowedFormats': 'Allowed formats: PDF, ZIP',
    'userPage.maxSize': 'Max. 50 MB per upload',
    'userPage.dropzoneHint': 'Click to select or drag your files here',
    'userPage.multipleDocs': 'Multiple documents allowed (e.g. contract, T&Cs, amendments, conditions)',
    'userPage.bestResultTitle': 'How to get the best result',
    'userPage.bestResult1': 'Please upload only one credit together with available attachments at a time.',
    'userPage.bestResult2': 'Scan documents as completely and legibly as possible',
    'userPage.bestResult3': 'Your data is stored encrypted',
    'userPage.bestResult4': 'You can add more credits later. Each upload is analysed separately.',
    'userPage.selectedFiles': 'Selected files',
    'userPage.docList1': 'Credit contract including any amendments (*required)',
    'userPage.docList2': 'Terms and conditions (optional)',
    'userPage.docList3': 'Conditions sheet (optional)',
    'userPage.docList4': 'Interest & fee overview (optional)',
    'userPage.docList5': 'Fee statements or account statements (optional)',
    'userPage.docList6': 'Any correspondence with the bank (optional)',
    'userPage.errorInvalidType': 'Invalid file types. Only PDF and ZIP are allowed.',
    'userPage.errorFileTooBig': 'Files too large. Maximum size: 50 MB per file.',
    'userPage.errorMaxFiles': 'Maximum {{max}} files per analysis. Only the first {{max}} were accepted.',
    'userPage.errorVerifyEmail': 'Please verify your email address before uploading documents.',
    'userPage.errorLoadJobs': 'Jobs could not be loaded. Check your connection.',
    'userPage.errorUploadFailed': 'Upload failed',
    'userPage.errorUnknown': 'Unknown error',
    'userPage.errorUploadFile': 'Error uploading {{name}}',
    'userPage.toastVerificationSent': 'Verification email sent. Please check your inbox.',
    'userPage.toastVerificationFailed': 'Verification email could not be resent.',
    'userPage.toastLawyerNotified': 'Lawyer was successfully notified. You will be contacted shortly.',
    'userPage.toastLawyerNotifyError': 'Error notifying lawyer. Please try again.',
    'userPage.eligibilityNoClaim': 'No claim to refund',
    'userPage.eligibilityReason': 'Reason: For at least one of the following questions the answer was "No":',
    'userPage.eligibilityManualReview': 'Manual legal review required (assessment based on criteria not conclusive)',
    'userPage.eligibilityPossibleClaim': 'Possible claim to refund – next step: notify lawyer',
    'userPage.eligibilityHeading': 'Claim to refund',
    'userPage.resetSelection': 'Reset selection',

    // Partner section
    'partner.title': 'Claim back unlawful credit fees now!',
    'partner.subtitle': 'Have you paid too much for your credit too?',
    'partner.p1': 'Check anonymously, quickly and free of charge whether your financing contains excessive costs or fees!',
    'partner.p2': 'Take 5 minutes now. Non-binding & no upfront costs!',
    'partner.cta': 'Learn more!',
    'partner.stat1': '9 out of 10 checked credit contracts contain intransparent clauses or impermissible fees!',
    'partner.stat2': 'Up to 100% of processing fees may be reclaimable!',
    'partner.stat3': 'Up to 30 years retroactively – even for loans already ended!',

    // Login
    'login.emailRequired': 'Email is required',
    'login.emailInvalid': 'Please enter a valid email address',
    'login.passwordRequired': 'Password is required',
    'login.passwordMinLength': 'Password must be at least 8 characters long',
    'login.passwordUppercase': 'Password must contain at least one uppercase letter',
    'login.passwordNumber': 'Password must contain at least one number',
    'login.passwordSpecial': 'Password must contain at least one special character',
    'login.title': 'Login',
    'login.subtitle': 'Sign in to your account to continue',
    'login.emailLabel': 'Email',
    'login.passwordLabel': 'Password',
    'login.loggingIn': 'Signing in...',
    'login.submit': 'Login',
    'login.noAccount': "Don't have an account?",
    'login.registerHere': 'Register here',
    'login.errorFailed': 'Login failed',
    'login.invalidCredentials': 'Invalid email or password.',
    'login.apiEmailPasswordRequired': 'Email and password are required',
    'login.apiTenantNotFound': 'Tenant not found',
    'login.apiInvalidSubdomain': 'Invalid subdomain',
    'login.networkHint':
      ' Try a hard refresh (Ctrl+Shift+R) to load the latest app. If it persists, the API may be unreachable or CORS may be blocking the response.',
    'login.buildIdTitle': 'Build identifier for support',

    // Register
    'register.subtitle': 'Register to start your free contract check',
    'register.firstName': 'First name',
    'register.lastName': 'Last name',
    'register.emailLabel': 'Email',
    'register.passwordLabel': 'Password',
    'register.confirmPassword': 'Confirm password',
    'register.passwordRequirementsHint':
      'Password requirements: at least 8 characters, 1 uppercase letter, 1 number, 1 special symbol',
    'register.registering': 'Registering...',
    'register.alreadyHaveAccount': 'Already have an account?',
    'register.loginHere': 'Log in here',
    'register.firstNameRequired': 'First name is required',
    'register.lastNameRequired': 'Last name is required',
    'register.confirmPasswordRequired': 'Please confirm your password',
    'register.passwordsMismatch': 'Passwords do not match',
    'register.captchaFailed': 'CAPTCHA verification failed. Please try again.',
    'register.captchaRequired': 'Please complete the CAPTCHA verification',
    'register.successToast':
      'Registration successful! Please confirm your email address before signing in.',
    'register.errorFailed': 'Registration failed',

    // Dashboard
    'dashboard.errorLoadJobs': 'Jobs could not be loaded. Check your connection.',
    'dashboard.fileTypeError': 'Only PDF or ZIP files allowed',
    'dashboard.fileSizeError': 'File size must be less than 50MB',
    'dashboard.selectFile': 'Please select a file',
    'dashboard.uploadFailed': 'Upload failed',

    // JobDetail
    'jobDetail.notFound': 'Job not found',
    'jobDetail.title': 'Job details',
    'jobDetail.jobId': 'Job ID:',
    'jobDetail.loading': 'Loading job details...',
    'jobDetail.backToDashboard': 'Back to Dashboard',
    'jobDetail.claimOutcome': 'Claim outcome',
    'jobDetail.noClaim': 'No claim',
    'jobDetail.reason': 'Reason: For at least one of the following questions the answer was "No":',
    'jobDetail.manualReview': 'Manual legal review recommended (assessment not conclusive).',
    'jobDetail.possibleClaim': 'Possible refund claim',
  },
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'de'; // Default to German
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback((key) => {
    const table = translations[language] || {};
    return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : key;
  }, [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook exported from same file as provider
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

